import React, { useEffect, useState, useRef } from 'react';
import mermaid from 'mermaid';
import './MermaidDiagram.css';
import { nordThemeVariables } from '../config/nordTheme';

interface MermaidDiagramProps {
  code: string;
}

const MermaidDiagram: React.FC<MermaidDiagramProps> = ({ code }) => {
  const [renderedSvg, setRenderedSvg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [currentTheme, setCurrentTheme] = useState<'default' | 'dark' | 'base'>('dark');
  const [zoom, setZoom] = useState(1);
  const svgContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Get theme from document
    const theme = document.documentElement.getAttribute('data-theme');
    const isNordTheme = theme === 'nord';

    // Initialize Mermaid with current theme and improved spacing
    mermaid.initialize({
      startOnLoad: false,
      theme: currentTheme,
      themeVariables: isNordTheme ? nordThemeVariables : undefined,
      securityLevel: 'loose',
      fontFamily: isNordTheme ? nordThemeVariables.fontFamily : 'inherit',
      flowchart: {
        useMaxWidth: false,
        htmlLabels: true,
        curve: 'basis',
        padding: isNordTheme ? 30 : 15,        // More padding for breathing room
        nodeSpacing: isNordTheme ? 80 : 60,    // More horizontal space
        rankSpacing: isNordTheme ? 80 : 60,    // More vertical space
        diagramPadding: isNordTheme ? 20 : 10
      }
    });
  }, [currentTheme]);

  useEffect(() => {
    const renderDiagram = async () => {
      if (!code || code.trim().length === 0) {
        setError('Kein Mermaid-Code vorhanden. Möglicherweise hat die API keinen Code generiert.');
        setRenderedSvg(null);
        return;
      }

      setError(null);
      console.log('Rendering Mermaid code:', code);

      try {
        // Generate unique ID
        const id = `mermaid-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        console.log('Generated ID:', id);

        // Render diagram
        console.log('Calling mermaid.render...');
        const { svg } = await mermaid.render(id, code);
        console.log('Mermaid rendered successfully! SVG length:', svg.length);

        // Simple approach: Find ALL text with "Ja"/"Nein" and style them
        const theme = document.documentElement.getAttribute('data-theme');

        if (theme === 'nord') {
          const parser = new DOMParser();
          const svgDoc = parser.parseFromString(svg, 'image/svg+xml');

          console.log('🎨 Applying colored backgrounds to Ja/Nein labels...');

          // Find all <g> groups that might contain labels
          const allGroups = svgDoc.querySelectorAll('g');
          console.log(`Found ${allGroups.length} groups in SVG`);

          // First, let's find all text elements to see what's in the SVG
          const allTextElements = svgDoc.querySelectorAll('text');
          console.log(`Found ${allTextElements.length} text elements`);
          allTextElements.forEach((txt, idx) => {
            const content = txt.textContent?.trim() || '';
            if (content) {
              console.log(`Text ${idx}: "${content}"`);
            }
          });

          allGroups.forEach((group) => {
            const textElement = group.querySelector('text');
            if (textElement) {
              const text = textElement.textContent?.toLowerCase().trim() || '';

              if (text === 'ja') {
                console.log('✅ Found "Ja" - styling...');
                const rect = group.querySelector('rect');
                if (rect) {
                  // Get current dimensions
                  const w = parseFloat(rect.getAttribute('width') || '0');
                  const h = parseFloat(rect.getAttribute('height') || '0');
                  const x = parseFloat(rect.getAttribute('x') || '0');
                  const y = parseFloat(rect.getAttribute('y') || '0');

                  console.log(`  Ja rect BEFORE: w=${w}, h=${h}, x=${x}, y=${y}`);

                  rect.setAttribute('fill', '#88C0D0');
                  rect.setAttribute('fill-opacity', '1');
                  rect.setAttribute('stroke', '#88C0D0');
                  rect.setAttribute('stroke-width', '3');
                  rect.setAttribute('rx', '8');

                  // Increase size
                  const newW = Math.max(w + 40, 70);
                  const newH = Math.max(h + 20, 36);
                  const newX = x - 20;
                  const newY = y - 10;

                  rect.setAttribute('width', newW.toString());
                  rect.setAttribute('height', newH.toString());
                  rect.setAttribute('x', newX.toString());
                  rect.setAttribute('y', newY.toString());

                  console.log(`  Ja rect AFTER: w=${newW}, h=${newH}, x=${newX}, y=${newY}`);
                } else {
                  console.log('  ❌ No rect found in group for "Ja"');
                }

                textElement.setAttribute('fill', '#2E3440');
                textElement.setAttribute('font-weight', '700');
                textElement.setAttribute('font-size', '15px');
              }
              else if (text === 'nein') {
                console.log('❌ Found "Nein" - styling...');
                const rect = group.querySelector('rect');
                if (rect) {
                  // Get current dimensions
                  const w = parseFloat(rect.getAttribute('width') || '0');
                  const h = parseFloat(rect.getAttribute('height') || '0');
                  const x = parseFloat(rect.getAttribute('x') || '0');
                  const y = parseFloat(rect.getAttribute('y') || '0');

                  console.log(`  Nein rect BEFORE: w=${w}, h=${h}, x=${x}, y=${y}`);

                  rect.setAttribute('fill', '#5E81AC');
                  rect.setAttribute('fill-opacity', '1');
                  rect.setAttribute('stroke', '#5E81AC');
                  rect.setAttribute('stroke-width', '3');
                  rect.setAttribute('rx', '8');

                  // Increase size
                  const newW = Math.max(w + 40, 70);
                  const newH = Math.max(h + 20, 36);
                  const newX = x - 20;
                  const newY = y - 10;

                  rect.setAttribute('width', newW.toString());
                  rect.setAttribute('height', newH.toString());
                  rect.setAttribute('x', newX.toString());
                  rect.setAttribute('y', newY.toString());

                  console.log(`  Nein rect AFTER: w=${newW}, h=${newH}, x=${newX}, y=${newY}`);
                } else {
                  console.log('  ❌ No rect found in group for "Nein"');
                }

                textElement.setAttribute('fill', '#ECEFF4');
                textElement.setAttribute('font-weight', '700');
                textElement.setAttribute('font-size', '15px');
              }
            }
          });

          const serializer = new XMLSerializer();
          const transformedSvg = serializer.serializeToString(svgDoc);
          setRenderedSvg(transformedSvg);
        } else {
          // For non-Nord themes, use SVG as-is
          setRenderedSvg(svg);
        }

        console.log('SVG stored in state');

        // Check after 100ms
        setTimeout(() => {
          console.log('=== 100ms later check ===');
          console.log('✅ SVG is in React state, should be visible now');
        }, 100);
      } catch (err: any) {
        console.error('Mermaid rendering error:', err);
        console.error('Error details:', err.message, err.stack);
        setError(`Fehler beim Rendern des Diagramms: ${err.message || 'Unbekannter Fehler'}`);
        setRenderedSvg(null);
      }
    };

    renderDiagram();
  }, [code, currentTheme]);

  // Add drag-and-drop functionality to nodes
  useEffect(() => {
    if (!renderedSvg || !svgContainerRef.current) return;

    const svgElement = svgContainerRef.current.querySelector('svg');
    if (!svgElement) return;

    let selectedNode: SVGGElement | null = null;
    let offset = { x: 0, y: 0 };

    // Build edge connection map
    const edgeConnections = new Map<string, Array<{ edge: SVGPathElement; edgeLabel?: SVGGElement; isSource: boolean; targetNodeId: string }>>();

    // Find ALL path elements first to see what we have
    const allPathsAny = svgElement.querySelectorAll('path');
    console.log(`🔍 Found ${allPathsAny.length} total path elements`);
    allPathsAny.forEach((path, idx) => {
      const classes = path.getAttribute('class') || '';
      const id = path.getAttribute('id') || '';
      console.log(`  Path ${idx}: class="${classes}", id="${id}"`);
    });

    // Find all edgeLabel groups to see their structure
    const allEdgeLabels = svgElement.querySelectorAll('g.edgeLabel');
    console.log(`🏷️ Found ${allEdgeLabels.length} edgeLabel groups`);
    allEdgeLabels.forEach((label, idx) => {
      const transform = label.getAttribute('transform') || '';
      const id = label.getAttribute('id') || '';

      // Try to find text in various ways
      const textElement = label.querySelector('text');
      const allText = textElement?.textContent || '';
      const allTspans = label.querySelectorAll('tspan');
      let tspanTexts: string[] = [];
      allTspans.forEach(t => {
        const txt = t.textContent || '';
        if (txt) tspanTexts.push(txt);
      });

      const innerHTML = label.innerHTML.substring(0, 200); // First 200 chars

      console.log(`  EdgeLabel ${idx}:`);
      console.log(`    id="${id}"`);
      console.log(`    transform="${transform}"`);
      console.log(`    text="${allText}"`);
      console.log(`    tspans=[${tspanTexts.join(', ')}]`);
      console.log(`    innerHTML="${innerHTML}..."`);
    });

    // Find all edges and their connections - try ALL path elements
    const allPaths = svgElement.querySelectorAll('path');
    console.log(`🔍 Analyzing ${allPaths.length} path elements for connections`);

    allPaths.forEach((path, idx) => {
      const pathElement = path as SVGPathElement;
      const pathId = pathElement.getAttribute('id') || '';

      // Skip if no ID or if it's just an arrowMarkerPath
      if (!pathId || pathId === '') {
        console.log(`  Path ${idx}: No ID, skipping`);
        return;
      }

      // Parse path ID format: "L_SourceName_TargetName_Index"
      const idMatch = pathId.match(/^L_(.+?)_(.+?)_\d+$/);

      if (idMatch) {
        const sourceName = idMatch[1];
        const targetName = idMatch[2];
        console.log(`  ✅ Path ${idx}: ID="${pathId}" => ${sourceName} -> ${targetName}`);

        // Find nodes by matching the name part in their IDs
        // Node IDs are like "flowchart-Start-0", so we search for nodes containing the name
        const allNodes = svgElement.querySelectorAll('g.node');
        let sourceNode: Element | null = null;
        let targetNode: Element | null = null;

        allNodes.forEach((node) => {
          const nodeId = node.getAttribute('id') || '';
          // Extract name from "flowchart-NodeName-Number"
          const nodeNameMatch = nodeId.match(/flowchart-(.+?)-\d+$/);
          if (nodeNameMatch) {
            const nodeName = nodeNameMatch[1];
            if (nodeName === sourceName) {
              sourceNode = node;
            }
            if (nodeName === targetName) {
              targetNode = node;
            }
          }
        });

        if (sourceNode && targetNode) {
          const sourceId = (sourceNode as SVGElement).getAttribute('id') || '';
          const targetId = (targetNode as SVGElement).getAttribute('id') || '';
          console.log(`    Found nodes: ${sourceId} -> ${targetId}`);

          // Find edge label by matching data-id attribute
          // Labels have an inner <g class="label" data-id="L_X_Y_Z">
          let edgeLabelGroup: SVGGElement | null = null;
          allEdgeLabels.forEach((label) => {
            const innerLabel = label.querySelector('g.label') as SVGElement;
            if (innerLabel) {
              const dataId = innerLabel.getAttribute('data-id');
              if (dataId === pathId) {
                edgeLabelGroup = label as SVGGElement;
                console.log(`    ✅ Found matching edgeLabel for ${pathId}`);
              }
            }
          });

          // Add to source node's connections
          if (!edgeConnections.has(sourceId)) {
            edgeConnections.set(sourceId, []);
          }
          edgeConnections.get(sourceId)!.push({
            edge: pathElement,
            edgeLabel: edgeLabelGroup || undefined,
            isSource: true,
            targetNodeId: targetId
          });

          // Add to target node's connections
          if (!edgeConnections.has(targetId)) {
            edgeConnections.set(targetId, []);
          }
          edgeConnections.get(targetId)!.push({
            edge: pathElement,
            edgeLabel: edgeLabelGroup || undefined,
            isSource: false,
            targetNodeId: sourceId
          });
        } else {
          console.log(`    ❌ Could not find nodes for ${sourceName} -> ${targetName}`);
        }
      } else {
        console.log(`  ❌ Path ${idx}: ID="${pathId}" doesn't match expected format`);
      }
    });

    console.log(`🖱️ Found ${edgeConnections.size} nodes with connections`);

    // Find all node groups
    const nodeGroups = svgElement.querySelectorAll('g.node');
    console.log(`🖱️ Making ${nodeGroups.length} nodes draggable...`);

    nodeGroups.forEach((node) => {
      const gNode = node as SVGGElement;
      const nodeId = gNode.getAttribute('id');
      console.log(`  Node ID: ${nodeId}`);
      gNode.style.cursor = 'move';

      const onMouseDown = (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        selectedNode = gNode;

        // Get current transform
        const transform = gNode.getAttribute('transform') || '';
        const match = transform.match(/translate\(([^,]+),([^)]+)\)/);
        const currentX = match ? parseFloat(match[1]) : 0;
        const currentY = match ? parseFloat(match[2]) : 0;

        // Calculate offset from node position to mouse position
        const rect = gNode.getBoundingClientRect();
        const svgRect = svgElement.getBoundingClientRect();

        offset = {
          x: (e.clientX - svgRect.left) / zoom - currentX,
          y: (e.clientY - svgRect.top) / zoom - currentY
        };

        console.log(`📍 Started dragging node at (${currentX}, ${currentY})`);
      };

      gNode.addEventListener('mousedown', onMouseDown as EventListener);
    });

    const getNodeCenter = (node: SVGGElement): { x: number; y: number } => {
      const transform = node.getAttribute('transform') || '';
      const match = transform.match(/translate\(([^,]+),([^)]+)\)/);
      const x = match ? parseFloat(match[1]) : 0;
      const y = match ? parseFloat(match[2]) : 0;
      return { x, y };
    };

    const updateConnectedEdges = (nodeId: string) => {
      console.log(`🔗 updateConnectedEdges called for node: ${nodeId}`);
      const connections = edgeConnections.get(nodeId);

      if (!connections) {
        console.log(`  ❌ No connections found for node ${nodeId}`);
        console.log(`  Available node IDs in map:`, Array.from(edgeConnections.keys()));
        return;
      }

      console.log(`  ✅ Found ${connections.length} connections for node ${nodeId}`);

      connections.forEach(({ edge, edgeLabel, isSource, targetNodeId }, idx) => {
        const sourceNodeId = isSource ? nodeId : targetNodeId;
        const targetNodeIdActual = isSource ? targetNodeId : nodeId;

        console.log(`    Connection ${idx}: ${sourceNodeId} -> ${targetNodeIdActual}`);

        // Find source and target nodes
        const sourceNode = svgElement.querySelector(`g.node#${sourceNodeId}`) as SVGGElement;
        const targetNode = svgElement.querySelector(`g.node#${targetNodeIdActual}`) as SVGGElement;

        if (!sourceNode || !targetNode) {
          console.log(`    ❌ Could not find nodes: source=${!!sourceNode}, target=${!!targetNode}`);
          return;
        }

        const sourceCenter = getNodeCenter(sourceNode);
        const targetCenter = getNodeCenter(targetNode);

        console.log(`    📍 Source: (${sourceCenter.x}, ${sourceCenter.y}), Target: (${targetCenter.x}, ${targetCenter.y})`);

        // Create a simple curved path
        const dx = targetCenter.x - sourceCenter.x;
        const dy = targetCenter.y - sourceCenter.y;
        const midX = sourceCenter.x + dx / 2;
        const midY = sourceCenter.y + dy / 2;

        // Bezier curve control points
        const cp1x = sourceCenter.x + dx * 0.3;
        const cp1y = sourceCenter.y;
        const cp2x = targetCenter.x - dx * 0.3;
        const cp2y = targetCenter.y;

        // Update path
        const newPath = `M${sourceCenter.x},${sourceCenter.y} C${cp1x},${cp1y} ${cp2x},${cp2y} ${targetCenter.x},${targetCenter.y}`;
        edge.setAttribute('d', newPath);
        console.log(`    ✅ Updated edge path`);

        // Update edge label position if it exists
        if (edgeLabel) {
          edgeLabel.setAttribute('transform', `translate(${midX}, ${midY})`);
          console.log(`    ✅ Updated edge label position`);
        }
      });
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!selectedNode) return;

      e.preventDefault();

      const svgRect = svgElement.getBoundingClientRect();

      // Calculate new position accounting for zoom
      const newX = (e.clientX - svgRect.left) / zoom - offset.x;
      const newY = (e.clientY - svgRect.top) / zoom - offset.y;

      // Update node position
      selectedNode.setAttribute('transform', `translate(${newX}, ${newY})`);

      // Update connected edges
      const nodeId = selectedNode.getAttribute('id');
      if (nodeId) {
        updateConnectedEdges(nodeId);
      }
    };

    const onMouseUp = () => {
      if (selectedNode) {
        console.log(`📍 Stopped dragging node`);
        selectedNode = null;
      }
    };

    // Add global event listeners
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);

    // Cleanup
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [renderedSvg, zoom]);

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.1, 2));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.1, 0.5));
  };

  const handleZoomReset = () => {
    setZoom(1);
  };

  return (
    <div className="mermaid-diagram-container">
      <div className="diagram-controls">
        <button onClick={handleZoomOut} title="Verkleinern">
          −
        </button>
        <span className="zoom-level">{Math.round(zoom * 100)}%</span>
        <button onClick={handleZoomIn} title="Vergrößern">
          +
        </button>
        <button onClick={handleZoomReset} title="Zurücksetzen">
          Reset
        </button>

        <div className="diagram-legend">
          <div className="legend-item">
            <div className="legend-box"></div>
            <span>Aktivität</span>
          </div>
          <div className="legend-item">
            <div className="legend-diamond"></div>
            <span>Entscheidung</span>
          </div>
        </div>
      </div>

      {error && (
        <div className="diagram-error">
          <p>{error}</p>
        </div>
      )}

      {renderedSvg && (
        <div
          ref={svgContainerRef}
          className="diagram-wrapper"
          style={{
            transform: `scale(${zoom})`,
            transformOrigin: 'top left'
          }}
          dangerouslySetInnerHTML={{ __html: renderedSvg }}
        />
      )}

      {!renderedSvg && !error && (
        <div className="diagram-wrapper">
          <p style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '2rem' }}>
            Lade Diagramm...
          </p>
        </div>
      )}
    </div>
  );
};

export default MermaidDiagram;
